import Footer from "../components/footer/Footer";
import {StoreContext} from "../stores/StoreLoader";
import AdminMenu from "../components/misc/AdminMenu";
import * as React from "react";
import SchoolBlocksHeader from "../components/header/SchoolBlocksHeader";
import NotificationContainer from "../components/blocks/SchoolBlocks/Notification/NotificationContainer";
import UserMenu from "../components/header/UserMenu";
import dynamic from "next/dynamic";
import {ErrorBoundary, rollbarConfig} from "../components/utilities/ErrorBoundary";
import {Provider} from '@rollbar/react';
import {getQueries} from "../utils/SchoolBlocksUtilities";
import {useContext, useMemo} from "react";
import SafeRouter from "../components/utilities/SafeRouter";
import {withoutLocaleInURI} from "../internationalization/i18nURI";
import URI from 'urijs';
import {NotificationContainer as ReactNotificationContainer} from '../components/notifications/NotificationContainer';
import SchoolFeedWidget from "../components/cleverfeed/SchoolFeedWidget";
import ClickableLink from "../components/utilities/ClickableLink";
import classNames from "classnames";
import {WebAppInstallPrompt} from "../components/header/WebAppInstallPrompt";
import OfflinePage from "./OfflinePage";
import {observer} from "mobx-react";
import {IView} from "../stores/InterfaceStore";
import {HTML5Backend} from "react-dnd-html5-backend";

const DndProvider = dynamic(() => import("react-dnd").then(module => module.DndProvider));

const pages = {
    [IView.DEFAULT]: dynamic(() => import("./MainGridPage")),
    [IView.ALPHA]: dynamic(() => import("./AlphaCollectionPage")),
    [IView.MAP]: dynamic(() => import("./MapFixedCollectionPage")),
    [IView.CLASSROOM]: dynamic(() => import("./MainGridPage")),
    [IView.CALENDAR]: dynamic(() => import("./CalendarPage")),
    [IView.PAGE]: dynamic(() => import("./PageBlockPage/PageBlockPage")),
    [IView.FOLLOWING]: dynamic(() => import("./FollowingGridPage")),
    [IView.SEARCH]: dynamic(() => import("./SearchGridPage")),
    [IView.ERROR]: dynamic(() => import("./ErrorPage")),
    [IView.LOGIN]: dynamic(() => import("./LoginPage")),
    [IView.USER_SETTINGS]: dynamic(() => import("./UserSettingsPage")),
    [IView.ORGANIZATION_SETTINGS]: dynamic(() => import("./OrganizationSettings/OrganizationSettings")),
}

const WelcomePage = dynamic(() => import("./WelcomePage"));

const showAdminMenu = ["default", "alpha", "map-fixed", "classroom", "calendar", "page"];

function DragAndDropProvider(props) {
    const {userStore} = useContext(StoreContext);
    if (userStore.isEditor) {
        return <DndProvider backend={HTML5Backend}>
            {props.children}
        </DndProvider>
    } else {
        return props.children;
    }
}

export const SchoolBlocksPageWrapper = observer((props: {
    view: string,
    notifications: IAlert[],
}): JSX.Element => {
    const {userStore, organizationStore, interfaceStore, styleStore} = useContext(StoreContext);

    const showHomepage = useMemo(() => {
        const queries = getQueries(interfaceStore.currentFullUrl || "");
        return (props.view === IView.DEFAULT &&
            organizationStore.organization.json_data?.settings?.appearance?.showWelcomePage &&
            URI(withoutLocaleInURI(interfaceStore.currentFullUrl || "")).path() === "/" &&
            (!queries.showHomepage || queries.showHomepage === "false")) as boolean;
    }, [interfaceStore.currentFullUrl])

    const showSchoolFeed = (organizationStore.organization.json_data?.settings?.appearance?.showSchoolFeedWidget === "everyone" ||
        (organizationStore.organization.json_data?.settings?.appearance?.showSchoolFeedWidget === "admins" && userStore.isAdmin));

    let content;
    if (showHomepage) {
        content = <div id={"welcome-page-container"}>
            <WelcomePage handleViewHomepage={async function () {
                await SafeRouter.setQuery("showHomepage", "true");
            }}/>
        </div>
    } else {
        let ContentComponent = pages[props.view];

        if (props.view === IView.OFFLINE) {
            ContentComponent = OfflinePage;
        } else if (!ContentComponent) {
            ContentComponent = pages.error;
        } else if (props.view === IView.ORGANIZATION_SETTINGS && !userStore.isAdmin) {
            ContentComponent = pages[IView.LOGIN];
        }

        let isIOS, isAndroidChrome;
        if (interfaceStore.ExecutionEnvironment.canUseDOM) {
            if (/iPad|iPhone|iPod/g.test(navigator.userAgent) && !window.MSStream) {
                isIOS = true;
                isAndroidChrome = false;
            } else if (/Android.*Chrome/g.test(navigator.userAgent) && !window.MSStream) {
                isIOS = false;
                isAndroidChrome = true;
            } else {
                isIOS = false;
                isAndroidChrome = false;
            }
        }

        const wrapperClassName = classNames({
            "isIOS": isIOS,
            "isAndroidChrome": isAndroidChrome,
            "isNotAndroidChrome": !isAndroidChrome,
            "isNotIOS": !isIOS,
            "sb-theme-simple_and_clean": styleStore.themeID === "1",
            "sb-theme-floating_blocks": styleStore.themeID === "2",
            "sb-theme-minimal": styleStore.themeID === "3",
        })

        content = <div className={wrapperClassName}>
            <div className={"sb-navigation-shown"}>
                <div id={"sb-content-canvas"}>
            <span id="skiptocontent">
                <ClickableLink href="#sb-main-content" tabIndex={1}>skip to main content</ClickableLink>
                <ClickableLink href="#schoolfeed" tabIndex={1}>skip to schoolfeed</ClickableLink>
            </span>
                    <ReactNotificationContainer/>
                    {organizationStore.organization.json_data?.settings?.webapp?.enabled && <ErrorBoundary>
                        <WebAppInstallPrompt/>
                    </ErrorBoundary>}
                    <div className="navigation-container">
                        <ErrorBoundary>
                            <SchoolBlocksHeader/>
                        </ErrorBoundary>
                    </div>
                    <div className="notification-container">
                        <ErrorBoundary>
                            <NotificationContainer notifications={props.notifications}
                                                   key={organizationStore.currentOrganization.id}/>
                        </ErrorBoundary>
                    </div>
                    <div className="user-menu-container"><ErrorBoundary>
                        <UserMenu/></ErrorBoundary>
                    </div>
                    <ErrorBoundary>
                        <ContentComponent {...props} />
                    </ErrorBoundary>
                    {showSchoolFeed && <ErrorBoundary>
                        <SchoolFeedWidget organization_id={organizationStore.currentOrganization.id}/>
                    </ErrorBoundary>}
                    <ErrorBoundary>
                        <Footer/>
                    </ErrorBoundary>
                    {userStore.editor && showAdminMenu.includes(props.view) && <ErrorBoundary>
                        <AdminMenu/>
                    </ErrorBoundary>}
                </div>
            </div>
        </div>
    }

    if (userStore.id) {
        rollbarConfig.payload.person = {
            id: userStore.id,
        }
    }

    return <Provider config={rollbarConfig}>
        <DragAndDropProvider>
            {content}
        </DragAndDropProvider>
    </Provider>
})
